import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { API_BASE_URL } from '../config/api-base-url.token';
import type {
  DashboardSummaryResponse,
  DocumentListResponse,
  DocumentPayload,
  DocumentStatus,
  DocumentSummary,
  SendReminderResponse,
  SendDocumentResponse,
  SignSessionResponse,
  PublicReviewSessionResponse,
  PublicDocumentConfigSaveResponse,
  PublicDocumentConfigSessionResponse,
  PublicDocumentConfigSendResponse,
  PublicDocumentConfigUrlResponse,
} from '../models/document.models';

@Injectable({ providedIn: 'root' })
export class DocumentApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  createDocument(payload: DocumentPayload): Observable<DocumentSummary> {
    return this.http.post<DocumentSummary>(`${this.baseUrl}/documents`, payload);
  }

  updateDocument(id: string, payload: Partial<DocumentPayload>): Observable<DocumentSummary> {
    return this.http.patch<DocumentSummary>(`${this.baseUrl}/documents/${id}`, payload);
  }

  sendDocument(id: string, sendAt?: string): Observable<SendDocumentResponse> {
    return this.http.post<SendDocumentResponse>(`${this.baseUrl}/documents/${id}/send`, {
      sendAt,
    });
  }

  revokeDocument(id: string, reason?: string): Observable<DocumentSummary> {
    return this.http.post<DocumentSummary>(`${this.baseUrl}/documents/${id}/revoke`, {
      reason,
    });
  }

  sendReminder(id: string): Observable<SendReminderResponse> {
    return this.http.post<SendReminderResponse>(`${this.baseUrl}/documents/${id}/remind`, {});
  }

  deleteDocument(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/documents/${id}`);
  }

  restoreDocument(id: string): Observable<DocumentSummary> {
    return this.http.post<DocumentSummary>(`${this.baseUrl}/documents/${id}/restore`, {});
  }

  deleteDocumentForever(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/documents/${id}/permanent`);
  }

  getDashboardSummary(): Observable<DashboardSummaryResponse> {
    return this.http.get<DashboardSummaryResponse>(`${this.baseUrl}/documents/dashboard-summary`);
  }

  listDocuments(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: DocumentStatus;
  }): Observable<DocumentListResponse> {
    return this.listDocumentsByPath('documents', params);
  }

  listDeletedDocuments(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: DocumentStatus;
  }): Observable<DocumentListResponse> {
    return this.listDocumentsByPath('documents/trash', params);
  }

  private listDocumentsByPath(
    path: 'documents' | 'documents/trash',
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      status?: DocumentStatus;
    },
  ): Observable<DocumentListResponse> {
    const queryParams: Record<string, string | number> = {
      page: params?.page ?? 1,
      limit: params?.limit ?? 20,
      search: params?.search ?? '',
    };
    const status = params?.status?.trim();
    if (status) {
      queryParams['status'] = status;
    }

    return this.http.get<DocumentListResponse>(`${this.baseUrl}/${path}`, {
      params: queryParams,
    });
  }

  getDocument(id: string): Observable<DocumentSummary> {
    return this.http.get<DocumentSummary>(`${this.baseUrl}/documents/${id}`);
  }

  downloadSourcePdf(fileId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/files/pdf/${fileId}`, {
      responseType: 'blob',
    });
  }

  downloadFinalPdf(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/documents/${id}/final-pdf`, {
      responseType: 'blob',
    });
  }

  downloadSignatureCertificate(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/documents/${id}/signature-certificate`, {
      responseType: 'blob',
    });
  }

  getSignSession(token: string): Observable<SignSessionResponse> {
    return this.http.get<SignSessionResponse>(`${this.baseUrl}/sign/${encodeURIComponent(token)}`);
  }

  getSignPdf(token: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/sign/${encodeURIComponent(token)}/pdf`, {
      responseType: 'blob',
    });
  }

  getPublicReviewSession(token: string): Observable<PublicReviewSessionResponse> {
    return this.http.get<PublicReviewSessionResponse>(`${this.baseUrl}/review/${encodeURIComponent(token)}`);
  }

  getPublicReviewPdf(token: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/review/${encodeURIComponent(token)}/pdf`, {
      responseType: 'blob',
    });
  }

  getPublicDocumentConfigSession(token: string): Observable<PublicDocumentConfigSessionResponse> {
    return this.http.get<PublicDocumentConfigSessionResponse>(
      `${this.baseUrl}/public/document-config/${encodeURIComponent(token)}`,
    );
  }

  getPublicDocumentConfigPdf(token: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/public/document-config/${encodeURIComponent(token)}/pdf`, {
      responseType: 'blob',
    });
  }

  savePublicDocumentConfigFields(
    token: string,
    payload: { fields: DocumentPayload['fields'] },
  ): Observable<PublicDocumentConfigSaveResponse> {
    return this.http.put<PublicDocumentConfigSaveResponse>(
      `${this.baseUrl}/public/document-config/${encodeURIComponent(token)}/fields`,
      payload,
    );
  }

  refreshPublicDocumentConfigUrl(documentId: string): Observable<PublicDocumentConfigUrlResponse> {
    return this.http.post<PublicDocumentConfigUrlResponse>(
      `${this.baseUrl}/public/documents/${encodeURIComponent(documentId)}/configure-fields-url`,
      {},
    );
  }

  sendPublicDocumentConfig(
    token: string,
    payload: { fields: DocumentPayload['fields']; sendAt?: string },
  ): Observable<PublicDocumentConfigSendResponse> {
    return this.http.post<PublicDocumentConfigSendResponse>(
      `${this.baseUrl}/public/document-config/${encodeURIComponent(token)}/send`,
      payload,
    );
  }

  submitSign(
    token: string,
    values: Array<{ fieldId: string; value: unknown }>,
    disclosureConsentAcceptedAt?: string,
  ): Observable<{
    status: string;
    completed: boolean;
  }> {
    return this.http.post<{
      status: string;
      completed: boolean;
    }>(`${this.baseUrl}/sign/${encodeURIComponent(token)}/submit`, {
      values,
      disclosureConsentAcceptedAt,
    });
  }

  declineSign(token: string, reason?: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.baseUrl}/sign/${encodeURIComponent(token)}/decline`, {
      reason,
    });
  }
}
